import { Breadcrumbs, Button, Link, styled, useTheme, useMediaQuery } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { Level } from "types";
import { UserNavigationContext } from "../../context/UserNavigationContext";
import { getNavigationTarget } from "../../utils/general";

type INavigationBreadcrumbs = {
    learningObjectTypology:  Level;
    previousPath: string;
    activeLabel?: string;
    ariaHidden?:boolean;
    tabIndex?:number
}

type BreadcrumbItem = {
  isActive: boolean;
  label: string;
  url: string;
}

export const NavigationBreadcrumbs = ({ learningObjectTypology, previousPath, activeLabel, ariaHidden,tabIndex=0 }: INavigationBreadcrumbs)=>{
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { navigation } = useContext(UserNavigationContext);
  const { target } = getNavigationTarget(navigation);
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const isTablet = useMediaQuery(theme.breakpoints.between("xs","sm"));

  const currentPath = window.location.href.includes("per-te") ? "per-te" : "esplora";

  // Estrae informazioni dall'URL corrente per ricostruire la gerarchia
  function parseCurrentUrl() {
    const currentUrl = window.location.pathname;
    const urlParts = currentUrl.split("/").filter(part => part);

    // Estrae IDs e tipologie dall'URL
    const dettaglioIndex = urlParts.indexOf("dettaglio");
    if (dettaglioIndex === -1) return null;

    let rootId = null;
    let courseId = null;
    let activityId = null;
    let pathTypology = null;
    let courseTypology = null;
    let activityTypology = null;

    // Parsing dell'URL per identificare la struttura gerarchica
    // Esempi di URL supportati:
    // /esplora/dettaglio/123/PATH
    // /esplora/dettaglio/456/COURSE
    // /esplora/dettaglio/789/root/123/ACTIVITY
    // /esplora/dettaglio/789/course/456/ACTIVITY
    // /esplora/dettaglio/789/course/456/root/123/ACTIVITY
    // /esplora/dettaglio/6408/course/6675/root/6866/DA_EXTERNAL_RES

    for (let i = dettaglioIndex + 1; i < urlParts.length; i++) {
      const current = urlParts[i];
      const next = urlParts[i + 1];

      if (current === "course" && next) {
        // /course/456 pattern
        courseId = next;
        courseTypology = "COURSE";
        i++; // Skip the next element as we've consumed it
      } else if (current === "root" && next) {
        // /root/123 pattern
        rootId = next;
        pathTypology = "PATH";
        i++; // Skip the next element as we've consumed it
      } else if (next && (next === "PATH" || next === "COURSE")) {
        // /id/PATH or /id/COURSE pattern
        if (next === "PATH") {
          rootId = current;
          pathTypology = "PATH";
        } else if (next === "COURSE") {
          courseId = current;
          courseTypology = "COURSE";
        }
        i++; // Skip the next element as we've consumed it
      } else if (i === urlParts.length - 1) {
        // Last element is the typology
        const typology = current;
        if (typology === "PATH") {
          // This is a standalone PATH
          if (!rootId && i > dettaglioIndex + 1) {
            rootId = urlParts[dettaglioIndex + 1];
            pathTypology = "PATH";
          }
        } else if (typology === "COURSE") {
          // This is a standalone COURSE
          if (!courseId && i > dettaglioIndex + 1) {
            courseId = urlParts[dettaglioIndex + 1];
            courseTypology = "COURSE";
          }
        } else {
          // This is an activity typology - check if it's a valid activity type
          const activityTypes = ["DA_VIDEO", "DA_SCORM", "DA_XAPI", "DA_EXTERNAL_RES", "DA_YOUTUBE", "DA_SURVEY"];
          const isActivityType = typology === "ACTIVITY" ||
                                 typology.startsWith("DA_") ||
                                 activityTypes.includes(typology);

          if (isActivityType) {
            activityTypology = typology;
            // Find the activity ID (first ID after dettaglio that's not course/root value)
            if (!activityId && i > dettaglioIndex + 1) {
              activityId = urlParts[dettaglioIndex + 1];
            }
          }
        }
      }
    }

    return {
      activityId,
      activityTypology: activityTypology || learningObjectTypology,
      courseId,
      courseTypology,
      pathTypology,
      rootId,
      section: urlParts[0]
    };
  }

  // Costruisce la lista completa dei breadcrumbs
  function buildBreadcrumbsHierarchy(): BreadcrumbItem[] {
    const breadcrumbs: BreadcrumbItem[] = [];
    const urlInfo = parseCurrentUrl();

    if (!urlInfo) {
      // Fallback al comportamento originale
      const urlSection = window.location.pathname.split("/").filter(Boolean)[0] ?? "";
      return [
        {
          isActive: false,
          label: getSectionLabel(urlSection) || t(currentPath === "per-te" ? "for_you" : "explore"),
          url: previousPath ?? target
        },
        {
          isActive: true,
          label: activeLabel ?? typeActualObject(learningObjectTypology) ?? "",
          url: ""
        }
      ];
    }

    const { section, rootId, courseId, activityId, pathTypology, courseTypology, activityTypology } = urlInfo;

    // 1. Sezione principale (esplora, per-te, ecc.)
    breadcrumbs.push({
      isActive: false,
      label: getSectionLabel(section),
      url: `/${section}`
    });

    // 2. Percorso (PATH) se presente
    if (rootId && pathTypology) {
      breadcrumbs.push({
        isActive: !courseId && !activityId && learningObjectTypology === "PATH",
        label: t("package"), // "Percorso"
        url: `/${section}/dettaglio/${rootId}/PATH`
      });
    }

    // 3. Corso (COURSE) se presente
    if (courseId && courseTypology && learningObjectTypology !== "PATH") {
      let courseUrl;
      if (rootId) {
        // Se esiste anche un percorso padre: /section/dettaglio/courseId/root/rootId/COURSE
        courseUrl = `/${section}/dettaglio/${courseId}/root/${rootId}/COURSE`;
      } else {
        // Solo corso: /section/dettaglio/courseId/COURSE
        courseUrl = `/${section}/dettaglio/${courseId}/COURSE`;
      }

      breadcrumbs.push({
        // isActive: !activityId && learningObjectTypology === "COURSE",
        isActive: learningObjectTypology === "COURSE",
        label: t("course"), // "Corso"
        url: courseUrl
      });
    }

    // 4. Attività corrente se presente
    // if (activityId && activityTypology && activityTypology !== "PATH" && activityTypology !== "COURSE") {
    if (
      activityId && activityTypology
      && activityTypology !== "PATH" && activityTypology !== "COURSE"
      && learningObjectTypology !== "COURSE" && learningObjectTypology !== "PATH"
    ) {
      const activityTypes = ["DA_VIDEO", "DA_SCORM", "DA_XAPI", "DA_EXTERNAL_RES", "DA_YOUTUBE", "DA_SURVEY"];
      const isActivityType = activityTypology === "ACTIVITY" ||
                             activityTypology.startsWith("DA_") ||
                             activityTypes.includes(activityTypology);

      if (isActivityType) {
        breadcrumbs.push({
          isActive: true,
          label: t("learning_activity"), // "Attività"
          url: ""
        });
      }
    }

    return breadcrumbs;
  }

  function getSectionLabel(section: string): string {
    switch (section) {
    case "per-te": return t("for_you");
    case "esplora": return t("explore");
    case "community": return t("community");
    case "agenda": return t("agenda");
    case "bookmarks": return t("bookmarks");
    default: return t("explore");
    }
  }

  function typeActualObject(LOtype: Level) {
    if(LOtype !== "PATH" && LOtype !== "COURSE"){
      return t("learning_activity");
    }
    if(LOtype === "PATH") return t("package");
    if(LOtype === "COURSE") return t("course");
  }

  const breadcrumbItems = buildBreadcrumbsHierarchy();
  const parentItem = breadcrumbItems.findLast(item => !item.isActive);

  return (
    <BreadcrumbsWrapper $isMobile={ isMobile } $isTablet={ isTablet }>
      { parentItem && (
        <BreadcrumbsBanner
          aria-hidden={ ariaHidden ? ariaHidden : undefined }
          tabIndex={ tabIndex === 0 ? 0 : -1 }
          id="hero-banner-breadcrumbs"
          aria-label={ ariaHidden ? undefined : "breadcrumb" }
          separator={ <ChevronRight color={ theme.customColors.textPrimary } size="0.875rem" /> }
        >
          <BackButton
            aria-hidden={ ariaHidden ? ariaHidden : undefined }
            tabIndex={ tabIndex === 0 ? 0 : -1 }
            onClick={ () => navigate(breadcrumbItems[0].url) }
            startIcon={ <ChevronLeft size="0.875rem" /> }
            disableRipple
          >
            { t("go_back", "Indietro") }
          </BackButton>
        </BreadcrumbsBanner>
      ) }
      <BreadcrumbsBanner
        aria-hidden={ ariaHidden ? ariaHidden : undefined }
        tabIndex={ tabIndex === 0 ? 0 : -1 }
        id="hero-banner-breadcrumbs"
        aria-label={ ariaHidden ? undefined : "breadcrumb" }
        separator={ <ChevronRight color={ theme.customColors.textPrimary } size="0.875rem" /> }
      >
        { breadcrumbItems.map((item, index) => (
          item.isActive ? (
            <ActualCrumb
              tabIndex={ tabIndex === 0 ? 0 : -1 }
              key={ index }
              underline="none"
              textTransform="capitalize"
              fontSize="0.75rem"
            >
              { item.label }
            </ActualCrumb>
          ) : (
            <ParentCrumb
              tabIndex={ tabIndex === 0 ? 0 : -1 }
              key={ index }
              underline="none"
              href={ item.url }
              textTransform="capitalize"
            >
              { item.label }
            </ParentCrumb>
          )
        )) }
      </BreadcrumbsBanner>
    </BreadcrumbsWrapper>
  );
};


const ParentCrumb = styled(Link)(({ theme }) =>({
  "&:hover": {
    opacity: 1,
    textDecoration: "underline",
    textUnderlineOffset: "3px"
  },
  color: theme.customColors.textPrimary,
  cursor: "pointer",
  fontSize: "0.75rem",
  fontWeight: 400,
  opacity: 0.8,
  transition: "all 0.2s ease"
}));

const ActualCrumb = styled(Link)(({ theme }) =>({
  color: theme.customColors.textPrimary,
  cursor: "text",
  fontSize: "0.75rem",
  fontWeight: 700,
  opacity: 1
}));

const BreadcrumbsBanner = styled(Breadcrumbs)({
  lineHeight: "1.25rem"
});

const BackButton = styled(Button)(({ theme }) => ({
  color: theme.customColors.accentVariantA,
  fontSize: "0.75rem",
  fontWeight: 400,
  minWidth: "unset",
  padding: "0 0.25rem 0 0",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
    textUnderlineOffset: "3px"
  }
}));

const BreadcrumbsWrapper = styled("div")<{ $isTablet: boolean, $isMobile: boolean }>(
  ({ $isTablet, $isMobile }) => ({
    alignItems: "center",
    display: "flex",
    flexDirection: "row",
    gap: "0.75rem",
    paddingLeft: "2.5rem",
    paddingTop: "1.2rem",
    position: "absolute",
    zIndex: 999,
    ...(($isTablet || $isMobile) && {
      paddingLeft: "1rem",
      paddingTop: "0.8rem",
      paddingBottom: "0.5rem",
      width: "100%"
    })
  })
);
